import { decisionCustomFieldID, decisionDraftValue, decisionOptionSelected } from "../domain/decision-drafts.js";
import { decisionKindLabel, decisionResolutionText, decisionStatusLabel } from "../domain/conversation.js";
import { escapeHTML as sharedEscapeHTML } from "./ui-helpers.js";

export function renderDecisionStack(decisions, options = {}) {
  const items = Array.isArray(decisions) ? decisions : [];
  return `
    <div class="decision-stack">
      ${items.map((item) => renderDecisionCard(item, options)).join("")}
    </div>
  `;
}

function renderDecisionCard(item, options = {}) {
  const pending = item.state === "pending";
  const compact = Boolean(options.compact);
  const kindLabel = decisionKindLabel(item.kind);
  const resolutionText = decisionResolutionText(item, options.lookupTurn, { formatTimestamp: options.formatTimestamp });
  const title = item.summary || "Decision required";
  const promptText = item.prompt || item.detail || "Respond to continue the turn.";
  const showPrompt = promptText !== title;
  const detailText = item.detail || "";
  const isBoilerplateDetail = /requested input before continuing/i.test(detailText) || /respond to continue/i.test(detailText);
  const showDetail = detailText && detailText !== item.prompt && detailText !== title && !(compact && isBoilerplateDetail);

  return `
    <article class="decision-card decision-card--${escapeValue(options, item.state)}${compact ? " decision-card--compact" : ""}">
      <div class="decision-card__header">
        <div>
          ${compact ? "" : `<div class="decision-card__eyebrow">${escapeValue(options, kindLabel)}</div>`}
          <div class="decision-card__title">${escapeValue(options, title)}</div>
        </div>
        <span class="badge badge--decision-${escapeValue(options, item.state)}">${escapeValue(options, decisionStatusLabel(item, options.lookupTurn))}</span>
      </div>
      ${showPrompt ? `<div class="decision-card__prompt">${escapeValue(options, promptText)}</div>` : ""}
      ${showDetail ? `<div class="decision-card__detail">${escapeValue(options, detailText)}</div>` : ""}
      ${pending ? `
        <form class="decision-form" data-decision-id="${escapeValue(options, item.id)}" data-turn-id="${escapeValue(options, options.turnID || item.turn_id || "")}" data-thread-id="${escapeValue(options, options.threadID || item.thread_id || "")}">
          ${renderDecisionFields(item, options)}
          <div class="decision-form__actions">
            ${item.actions.map((action) => renderDecisionActionButton(item, action, options)).join("")}
          </div>
        </form>
      ` : `
        <div class="decision-card__resolution">${escapeValue(options, resolutionText)}</div>
      `}
    </article>
  `;
}

function renderDecisionFields(item, options = {}) {
  if (!item.fields.length) {
    return "";
  }
  return `
    <div class="decision-form__fields">
      ${item.fields.map((field) => renderDecisionField(item, field, options)).join("")}
    </div>
  `;
}

function renderDecisionField(item, field, options = {}) {
  const decisionDrafts = options.decisionDrafts || {};
  const value = decisionDraftValue(decisionDrafts, item.id, field.id, field.multiple ? [] : "");
  const customFieldKey = decisionCustomFieldID(field.id);
  const customValue = decisionDraftValue(decisionDrafts, item.id, customFieldKey, "");
  const fieldID = `decision-${item.id}-${field.id}`;
  const customFieldID = `decision-${item.id}-${field.id}-custom`;
  const label = String(field.label || field.id || "Input");
  const placeholder = String(field.placeholder || "");
  const required = Boolean(field.required);
  const type = String(field.type || "text").toLowerCase();

  if (type === "textarea") {
    return `
      <div class="field decision-field">
        <label for="${escapeValue(options, fieldID)}">${escapeValue(options, label)}</label>
        <textarea id="${escapeValue(options, fieldID)}" name="${escapeValue(options, field.id)}" data-decision-field="${escapeValue(options, field.id)}" placeholder="${escapeValue(options, placeholder)}" ${required ? "required" : ""}>${escapeValue(options, value)}</textarea>
      </div>
    `;
  }
  if (type === "radio") {
    return `
      <fieldset class="field decision-field decision-field--radio">
        ${label !== field.id ? `<legend>${escapeValue(options, label)}</legend>` : ""}
        <div class="decision-choice-list">
          ${(field.options || []).map((option) => {
    const isChecked = String(value) === String(option.id);
    const rec = option.recommended ? " <span class=\"decision-choice__rec\">recommended</span>" : "";
    return `
            <label class="decision-choice${isChecked ? " decision-choice--selected" : ""}">
              <input type="radio" name="${escapeValue(options, field.id)}" value="${escapeValue(options, option.id)}" data-decision-field="${escapeValue(options, field.id)}" ${isChecked ? "checked" : ""}>
              <span class="decision-choice__label">${escapeValue(options, option.label || option.id)}${rec}</span>
            </label>`;
  }).join("")}
        </div>
        ${field.custom ? `
          <div class="decision-choice__custom">
            <label for="${escapeValue(options, customFieldID)}">Or type your own answer</label>
            <input id="${escapeValue(options, customFieldID)}" type="text" name="${escapeValue(options, customFieldKey)}" data-decision-field="${escapeValue(options, customFieldKey)}" value="${escapeValue(options, customValue)}" placeholder="${escapeValue(options, placeholder || "Type your answer")}">
          </div>
        ` : ""}
      </fieldset>
    `;
  }
  if (type === "select") {
    return `
      <div class="field decision-field">
        <label for="${escapeValue(options, fieldID)}">${escapeValue(options, label)}</label>
        <select id="${escapeValue(options, fieldID)}" name="${escapeValue(options, field.id)}" data-decision-field="${escapeValue(options, field.id)}" ${field.multiple ? "multiple" : ""}>
          ${(field.options || []).map((option) => `
            <option value="${escapeValue(options, option.id)}" ${decisionOptionSelected(value, option.id) ? "selected" : ""}>${escapeValue(options, option.label || option.id)}</option>
          `).join("")}
        </select>
        ${field.custom ? `
          <div class="decision-choice__custom">
            <label for="${escapeValue(options, customFieldID)}">Or type your own answer</label>
            <input id="${escapeValue(options, customFieldID)}" type="text" name="${escapeValue(options, customFieldKey)}" data-decision-field="${escapeValue(options, customFieldKey)}" value="${escapeValue(options, customValue)}" placeholder="${escapeValue(options, placeholder || "Type your answer")}">
          </div>
        ` : ""}
      </div>
    `;
  }
  if (type === "checkbox" || type === "boolean") {
    return `
      <label class="decision-toggle">
        <input type="checkbox" name="${escapeValue(options, field.id)}" data-decision-field="${escapeValue(options, field.id)}" ${value ? "checked" : ""}>
        <span>${escapeValue(options, label)}</span>
      </label>
    `;
  }
  return `
    <div class="field decision-field">
      <label for="${escapeValue(options, fieldID)}">${escapeValue(options, label)}</label>
      <input id="${escapeValue(options, fieldID)}" type="${type === "secret" ? "password" : "text"}" name="${escapeValue(options, field.id)}" data-decision-field="${escapeValue(options, field.id)}" value="${escapeValue(options, value)}" placeholder="${escapeValue(options, placeholder)}" ${required ? "required" : ""}>
    </div>
  `;
}

function renderDecisionActionButton(item, action, options = {}) {
  const actionID = String(action.id || "").trim();
  const label = String(action.label || actionID || "Submit");
  const style = String(action.style || "").trim().toLowerCase();
  const classes = ["button", "button--sm"];
  if (style === "primary") {
    classes.push("button--primary");
  } else if (style === "danger") {
    classes.push("button--danger");
  } else {
    classes.push("button--ghost");
  }
  if (actionID === "cancel") {
    return `<button class="${classes.join(" ")}" type="button" data-action="decision-cancel" data-decision-id="${escapeValue(options, item.id)}">${escapeValue(options, label)}</button>`;
  }
  return `<button class="${classes.join(" ")}" type="button" data-action="decision-respond" data-decision-id="${escapeValue(options, item.id)}" data-decision-action="${escapeValue(options, actionID || item.default_action || "submit")}">${escapeValue(options, label)}</button>`;
}

function escapeValue(options, value) {
  const escapeHTML = typeof options.escapeHTML === "function" ? options.escapeHTML : sharedEscapeHTML;
  return escapeHTML(value);
}
